import React, { useState, useRef, useEffect } from 'react';
import { MapPin, ChevronDown, Check, RefreshCw } from 'lucide-react';
import { useLocation as useLocationContext } from '../contexts/LocationContext';

const COUNTRIES = [
  { code: 'IN', name: 'India', currency: 'INR', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: 'US', name: 'United States', currency: 'USD', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: 'GB', name: 'United Kingdom', currency: 'GBP', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'AE', name: 'UAE', currency: 'AED', flag: '\u{1F1E6}\u{1F1EA}' },
  { code: 'SG', name: 'Singapore', currency: 'SGD', flag: '\u{1F1F8}\u{1F1EC}' },
  { code: 'AU', name: 'Australia', currency: 'AUD', flag: '\u{1F1E6}\u{1F1FA}' },
  { code: 'CA', name: 'Canada', currency: 'CAD', flag: '\u{1F1E8}\u{1F1E6}' },
  { code: 'JP', name: 'Japan', currency: 'JPY', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: 'TH', name: 'Thailand', currency: 'THB', flag: '\u{1F1F9}\u{1F1ED}' },
  { code: 'EU', name: 'Europe', currency: 'EUR', flag: '\u{1F1EA}\u{1F1FA}' },
];

const CURRENCIES = [
  { code: 'INR', symbol: '\u20B9', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '\u00A3', name: 'British Pound' },
  { code: 'EUR', symbol: '\u20AC', name: 'Euro' },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'JPY', symbol: '\u00A5', name: 'Japanese Yen' },
  { code: 'THB', symbol: '\u0E3F', name: 'Thai Baht' },
];

export function LocationDropdown() {
  const { country, city, currency, currencySymbol, updateCity, updateCurrency, refreshLocation } = useLocationContext();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('location');
  const dropdownRef = useRef(null);
  const [customCity, setCustomCity] = useState('');

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCountrySelect = (c) => {
    updateCurrency(c.currency);
    updateCity('');
    setIsOpen(false);
  };

  const handleCurrencySelect = (c) => {
    updateCurrency(c.code);
    setIsOpen(false);
  };

  const handleCitySubmit = (e) => {
    e.preventDefault();
    if (customCity.trim()) {
      updateCity(customCity.trim());
      setCustomCity('');
      setIsOpen(false);
    }
  };

  const displayLocation = city || country || 'Set Location';
  const currentCountry = COUNTRIES.find(c => c.name === country || c.code === country);
  const displayFlag = currentCountry?.flag || '\u{1F30D}';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 transition-all text-sm font-medium text-gray-700"
        style={{ borderRadius: '9999px' }}
        data-testid="location-dropdown-trigger"
      >
        <span className="text-base">{displayFlag}</span>
        <span className="hidden sm:inline max-w-[100px] truncate">{displayLocation}</span>
        <span className="text-xs text-gray-500">({currencySymbol})</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-72 bg-white border border-gray-200 shadow-xl z-[100] overflow-hidden"
          style={{ borderRadius: '1rem' }}
          data-testid="location-dropdown-menu"
        >
          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            {[
              { key: 'location', label: 'Location' },
              { key: 'currency', label: 'Currency' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 py-3 text-sm font-semibold transition-all ${
                  activeTab === tab.key
                    ? 'text-teal-600 border-b-2 border-teal-500'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="max-h-64 overflow-y-auto p-2">
            {activeTab === 'location' && (
              <>
                {/* Custom City Input */}
                <form onSubmit={handleCitySubmit} className="p-2 mb-1">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Enter city name..."
                      value={customCity}
                      onChange={(e) => setCustomCity(e.target.value)}
                      className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 text-sm outline-none focus:border-teal-400 transition-all"
                      style={{ borderRadius: '0.5rem' }}
                      data-testid="location-city-input"
                    />
                    <button
                      type="submit"
                      className="px-3 py-2 bg-teal-500 text-white text-sm font-semibold hover:bg-teal-600 transition-all"
                      style={{ borderRadius: '0.5rem' }}
                      data-testid="location-city-submit"
                    >
                      Set
                    </button>
                  </div>
                </form>

                {/* Auto-detect */}
                <button
                  onClick={() => { refreshLocation(); setIsOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-600 hover:bg-teal-50 hover:text-teal-700 transition-all"
                  style={{ borderRadius: '0.5rem' }}
                  data-testid="location-auto-detect"
                >
                  <RefreshCw size={16} />
                  <span>Auto-detect my location</span>
                </button>

                <div className="border-t border-gray-100 my-1" />

                {/* Country List */}
                {COUNTRIES.map(c => (
                  <button
                    key={c.code}
                    onClick={() => handleCountrySelect(c)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-all ${
                      (country === c.name || country === c.code)
                        ? 'bg-teal-50 text-teal-700 font-semibold'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    style={{ borderRadius: '0.5rem' }}
                    data-testid={`location-country-${c.code.toLowerCase()}`}
                  >
                    <span className="text-lg">{c.flag}</span>
                    <span className="flex-1 text-left">{c.name}</span>
                    {(country === c.name || country === c.code) && <Check size={16} className="text-teal-600" />}
                  </button>
                ))}
              </>
            )}

            {activeTab === 'currency' && (
              <>
                {CURRENCIES.map(c => (
                  <button
                    key={c.code}
                    onClick={() => handleCurrencySelect(c)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-all ${
                      currency === c.code
                        ? 'bg-teal-50 text-teal-700 font-semibold'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    style={{ borderRadius: '0.5rem' }}
                    data-testid={`currency-${c.code.toLowerCase()}`}
                  >
                    <span className="text-base font-bold w-8">{c.symbol}</span>
                    <span className="flex-1 text-left">{c.name}</span>
                    <span className="text-gray-400 text-xs">{c.code}</span>
                    {currency === c.code && <Check size={16} className="text-teal-600" />}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
